import { FC, HTMLAttributeAnchorTarget, MouseEvent, ReactNode, useEffect, useMemo } from 'react';

import { Button, ButtonProps } from '@mui/material';

import { addClassName, dataLayerConfig, replaceUrlContentVars } from '@/utils';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@/context';
import { MOTTO_ALIGNERS_PATH, SCHEDULE_PATH } from '@/constants';
import { Maybe, AnalyticsContext } from '@/types/generated';
import { getInnerTextForAnalytics } from '@/utils/getInnerTextForAnalytics/getInnerTextForAnalytics';

export interface ITAGButton extends ButtonProps {
	target?: HTMLAttributeAnchorTarget;
	dataTestId?: string;
	analyticsContext?: Maybe<AnalyticsContext>;
	component?: React.ElementType;
}

const getRegularScheduleHref = (href: string, pathname: string, facilityCode?: string): string => {
	const isSpanishDirectory = pathname.includes('espanol');
	const hasTrailingSlash = href.endsWith('/');
	const slashString = hasTrailingSlash ? '' : '/';
	const localizedHref = isSpanishDirectory && !href.includes('es-US') ? `${href}${slashString}es-US/` : href;
	return facilityCode && !localizedHref.includes('fc') ? `${localizedHref}?fc=${facilityCode}` : localizedHref;
};

const getMottoScheduleHref = (href: string, facilityCode?: string): string => {
	// If the URL already contains the mottoaligners path, return it as-is
	if (href.includes(`/${MOTTO_ALIGNERS_PATH}`)) return href;
	const transformedHref = href.endsWith('/') ? href.slice(0, -1) : href;
	return facilityCode && !href.includes('fc')
		? `${transformedHref}/${MOTTO_ALIGNERS_PATH}?fc=${facilityCode}`
		: `${transformedHref}/${MOTTO_ALIGNERS_PATH}`;
};

const getHref = (pathname: string, href: string, facilityCode?: string) => {
	const isUserOnMottoAligners =
		pathname.includes(MOTTO_ALIGNERS_PATH) || globalThis?.location?.hostname?.includes('mottoclearaligners');
	const isHrefForSchedule = href.includes(SCHEDULE_PATH);
	if (!isHrefForSchedule) return href;
	if (isUserOnMottoAligners) return getMottoScheduleHref(href, facilityCode);
	return getRegularScheduleHref(href, pathname, facilityCode);
};

const getLabel = (children: ReactNode, pathname: string, href: string | undefined) => {
	const isSpanishDirectory = pathname.includes('espanol');
	const isHrefForSchedule = href?.includes(SCHEDULE_PATH);
	if (isHrefForSchedule && isSpanishDirectory) {
		return <span>Haz una cita</span>;
	}
	return children;
};

const TAGButton: FC<ITAGButton> = ({ children, dataTestId, href, analyticsContext, onClick, ...restProps }) => {
	const { facilityCode, officeInfo } = useFacilityContext();
	const { officeInfo: officeLocationInfo } = useOfficeLocationContext();
	const { config } = useAppContext();
	if (href) {
		href = replaceUrlContentVars(href, officeInfo, officeLocationInfo, config);
	}

	const pathname = globalThis.location?.pathname ?? '';
	const finalChildren = getLabel(children, pathname, href);

	const updatedHref = useMemo(() => {
		if (href) {
			return getHref(pathname, href, facilityCode);
		}
		return undefined;
	}, [href, facilityCode, pathname]);

	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		if (analyticsContext?.event) {
			dataLayerConfig({
				...analyticsContext,
				link_url: analyticsContext.linkUrl ?? updatedHref,
				page_destination_url: analyticsContext.linkUrl ?? updatedHref ?? '',
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		}

		if (config.name === 'ClearChoice' && analyticsContext?.event === 'mapbox_search_selection') {
			// default schedule_cta_click event
			dataLayerConfig({
				event: 'schedule_cta_click',
				brand: config.name,
				page_destination_url: updatedHref ?? '',
				click_name: getInnerTextForAnalytics(finalChildren),
				click_position: analyticsContext?.clickPosition ?? 'page',
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		} else if (analyticsContext?.event !== 'navigation_click_event') {
			// default data layer push regardless of analyticsContext existence
			dataLayerConfig({
				event: 'navigation_click_event',
				brand: config.name,
				page_destination_url: updatedHref ?? '',
				link_click_location: analyticsContext?.clickPosition ?? 'page',
				link_text: getInnerTextForAnalytics(finalChildren),
				website_selected_facility_id: officeInfo?.code ?? '',
				website_selected_facility_name: officeInfo?.name ?? '',
			});
		}

		if (onClick) {
			onClick(e);
		}

		return undefined;
	};

	useEffect(() => {
		if (updatedHref?.startsWith('tel')) {
			dataLayerConfig({
				event: 'invoca_page_load',
				brand: config.name,
				website_selected_facility_id: officeInfo?.code || '',
				website_selected_facility_name: officeInfo?.name || '',
				invoca_phone_number: updatedHref
					.replace(/\D/g, '')
					.replace(/^1/, '')
					.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3'),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatedHref]);

	return (
		<Button
			{...restProps}
			href={updatedHref ?? href}
			className={addClassName(href)}
			onClick={(e) => handleClick(e)}
			data-testid={dataTestId}
		>
			{finalChildren}
		</Button>
	);
};

export default TAGButton;
